import { useForm } from 'react-hook-form'
import clsx from 'clsx'
import { useEffect, useState, type FC } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'

import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'app/KinerjaPSPegawaiPage/components/Form'
import {
  PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_FIELD_TYPES,
  PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_FIELD_TYPES_STRUCTURE,
} from 'app/KinerjaPSPegawaiPage/constants/praktikKinerja'
import CardPanduan from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/CardPanduan'
import CardJadwalDiskusi from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/CardJadwalDiskusi'
import DialogConfirmation from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/DialogConfirmation'

import type {
  TPraktikKinerjaPendampinganFieldType,
  TPraktikKinerjaPendampinganFormValue,
} from 'types/kinerja-ps-pegawai'
import type { TUsePelaksanaanPraktikKinerjaPS } from 'app/KinerjaPSPegawaiPage/hooks/usePelaksanaanPraktikKinerjaPS'
import type { FieldPath, SubmitHandler, UseFormReturn } from 'react-hook-form'

type CardFormProps = {
  formValues: TUsePelaksanaanPraktikKinerjaPS<
    TPraktikKinerjaPendampinganFormValue,
    {}
  >
  onSubmitSuccess: () => void
}

const MAX_LENGTH_INPUT = 250
const MSG_ERR_REQUIRED = 'Wajib diisi.'
const MSG_ERR_MAX_LENGTH = `Maksimal ${MAX_LENGTH_INPUT} karakter.`
const MSG_ERR_SUBMIT = 'Gagal kirim. Coba lagi.'
const MSG_SUCCESS_SUBMIT = 'Berhasil kirim!'

const defaultValues: TPraktikKinerjaPendampinganFormValue = Object.keys(
  PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_FIELD_TYPES
).reduce((acc, key) => {
  acc[key as TPraktikKinerjaPendampinganFieldType] = ''
  return acc
}, {} as TPraktikKinerjaPendampinganFormValue)

const FField: FC<{
  form: UseFormReturn<TPraktikKinerjaPendampinganFormValue, any>
  name: FieldPath<TPraktikKinerjaPendampinganFormValue>
  question: string
  canEdit: boolean
}> = ({ form, name, question, canEdit = false }) => {
  return (
    <FormField
      control={form.control}
      name={name}
      rules={{
        required: MSG_ERR_REQUIRED,
        maxLength: {
          value: MAX_LENGTH_INPUT,
          message: MSG_ERR_MAX_LENGTH,
        },
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{question}</FormLabel>
          <FormControl>
            <Textarea
              placeholder="Ketik di sini"
              {...field}
              isDisabled={!canEdit}
              aria-label={`Answer for ${question}`}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

const FormActions: FC<{
  isFormDirty: boolean
  isSubmitting: boolean
  onSubmitAsDraft: () => void
}> = ({ isFormDirty = false, isSubmitting = false, onSubmitAsDraft }) => {
  const shouldBeDisabled = isSubmitting || !isFormDirty

  return (
    <div className="fixed bottom-0 left-0 right-0 z-10 w-full px-24 py-4 bg-white border-t border-subdued">
      <div className="flex justify-end gap-4 px-3 mx-auto max-w-7xl">
        <Button
          color="white"
          size="md"
          isLoading={isSubmitting}
          disabled={shouldBeDisabled}
          className={clsx(
            '!px-6',
            shouldBeDisabled && '!bg-gray-30 !text-gray-50 !cursor-not-allowed'
          )}
          onClick={() => onSubmitAsDraft?.()}
        >
          Simpan draf
        </Button>

        <Button
          color="blue"
          size="md"
          isLoading={isSubmitting}
          disabled={shouldBeDisabled}
          className={clsx(
            '!px-6',
            shouldBeDisabled && '!bg-gray-30 !text-gray-50 !cursor-not-allowed'
          )}
          type="submit"
        >
          Kirim
        </Button>
      </div>
    </div>
  )
}

const CardForm: FC<CardFormProps> = ({
  formValues: {
    isSuccessfullyFetchedFormValues,
    dataFormValues,
    isPostingFormValues,
    submitFormValues,
    parsedFormValues,
  },
  onSubmitSuccess,
}) => {
  const toast = useToast()

  const form = useForm<TPraktikKinerjaPendampinganFormValue>({
    defaultValues,
  })

  const isSubmitting = isPostingFormValues

  // Perform save action, either as draft or final submission
  const save = async ({ saveAsDraft = false }: { saveAsDraft: boolean }) => {
    try {
      await submitFormValues({ values: form.getValues(), saveAsDraft })

      toast({ message: MSG_SUCCESS_SUBMIT, type: 'success' })
      onSubmitSuccess?.()
    } catch (e) {
      console.error(e)
      toast({ message: MSG_ERR_SUBMIT })
    }
  }

  // Wrapper for form submission
  const onSubmitAsDraft = () => save({ saveAsDraft: true })

  const [isDialogConfirmationOpen, setIsDialogConfirmationOpen] =
    useState(false)

  const onSubmit: SubmitHandler<TPraktikKinerjaPendampinganFormValue> = () => {
    setIsDialogConfirmationOpen(true)
  }

  // Set form values from fetched data
  useEffect(() => {
    if (isSuccessfullyFetchedFormValues && dataFormValues) {
      // Set form values
      Object.keys(defaultValues).map(
        (key: TPraktikKinerjaPendampinganFieldType) => {
          form.setValue(key, parsedFormValues[key] || defaultValues[key], {
            shouldDirty: true,
          })
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessfullyFetchedFormValues])

  return (
    <div className="flex flex-row items-start gap-6">
      <CardPanduan className="flex-none w-[329px]" />

      <SimpleCard className="flex-1 !rounded mb-24">
        {/* Simple Card Header */}
        <div className="flex flex-col gap-1 p-6 border-b rounded-t bg-blue-5 border-b-surface-subdued">
          <Text variant="heading-lg">
            Isi sembari melakukan pendampingan (1-3 bulan)
          </Text>
          <Text>
            Pengisian menjadi topik untuk Diskusi Tindak Lanjut bersama Tim
            Kinerja.
          </Text>
        </div>

        {/* Simple Card Content */}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6 p-6"
          >
            {PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_FIELD_TYPES_STRUCTURE.map(
              ({ title, fields }, index) => (
                <section className="space-y-3" key={index}>
                  <Text variant="heading-md" color="default">
                    {title}
                  </Text>

                  {fields?.map((field, fieldIndex) => (
                    <FField
                      key={`${index}-${fieldIndex}`}
                      form={form}
                      canEdit={!isSubmitting}
                      name={field}
                      question={
                        PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_FIELD_TYPES[
                          field as TPraktikKinerjaPendampinganFieldType
                        ]
                      }
                    />
                  ))}
                </section>
              )
            )}

            <CardJadwalDiskusi />

            <FormActions
              isFormDirty={form.formState.isDirty}
              isSubmitting={isSubmitting}
              onSubmitAsDraft={onSubmitAsDraft}
            />
          </form>
        </Form>
      </SimpleCard>

      <DialogConfirmation
        isSubmitting={isSubmitting}
        isOpen={isDialogConfirmationOpen}
        onConfirm={() => {
          save({ saveAsDraft: false }).finally(() => {
            setIsDialogConfirmationOpen(false)
          })
        }}
        onBack={() => setIsDialogConfirmationOpen(false)}
      />
    </div>
  )
}

export default CardForm
